import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, forwardRef, inject } from "@angular/core";
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";
import { PasswordModule } from "primeng/password";
import { Subject, Subscription, debounceTime } from "rxjs";
import { onChangeAndTouchFn } from "../input/interfaces/input-modak.types";

@Component({
  selector: "mdk-password-input",
  templateUrl: "./password-input.component.html",
  styleUrls: [
    "./styles/password-input.component.mobile.scss",
    "./styles/password-input.component.desktop.scss",
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PasswordInputModakComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [PasswordModule, CommonModule, FormsModule],
})
export class PasswordInputModakComponent implements OnInit, OnDestroy, ControlValueAccessor {
  //#region [---- [INPUTS] ----]
  @Input() label: string;
  @Input() helperText: string;
  @Input() feedback: boolean = false;
  @Input() toggleMask: boolean = false;
  @Input() isInvalid: boolean;
  @Input() labelError: string;
  //#endregion

  //#region [---- [DEPENDENCIES] ----]
  private cdr:ChangeDetectorRef = inject(ChangeDetectorRef);
  //#endregion

  //#region [---- [PROPERTIES] ----]
  public passwordValue: string = "";
  public isDisabled: boolean;
  private debouncerSubscription: Subscription;
  private _onChangeFn: onChangeAndTouchFn = (_value) => {};
  private _onTouchFn: onChangeAndTouchFn = (_value) => {};
  private debouncer:Subject<string> = new Subject<string>();
  //#endregion

  //#region [---- LIFE CYCLES ----]
  ngOnInit(): void {
    this.initDebouncer();
  }

  ngOnDestroy(): void {
    this.debouncerSubscription.unsubscribe();
  }
  //#endregion

  //#region [---- CVA METHODS ----]
  writeValue(value: string): void {
    if (typeof value === "string") this.passwordValue = value;
    this.cdr.detectChanges();
  }

  registerOnChange(fn: onChangeAndTouchFn): void {
    this._onChangeFn = fn;
  }

  registerOnTouched(fn: onChangeAndTouchFn): void {
    this._onTouchFn = fn;
  }

  setDisabledState(state: boolean): void {
    this.isDisabled = state;
    this.cdr.detectChanges();
  }
  //#endregion

  //#region [---- EVENTS ----]
  onChange(){
    this.debouncer.next(this.passwordValue)
  }

  onBlur(){
    this._onTouchFn(this.passwordValue);
  }
  //#endregion

  //#region [---- LOGIC ----]
  private initDebouncer(): void {
    this.debouncerSubscription = this.debouncer
      .pipe(debounceTime(300))
      .subscribe((passwordValue) => {
        this._onChangeFn(passwordValue);
        this._onTouchFn(passwordValue);
      });
  }
  //#endregion
}
